import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { motion } from "framer-motion";
import Button from "../../components/ACS-Dashboard/ACS-Blocks/Button";
import ascendLogo from "../../assets/ACS-Images/Ascend-Logo.png";

const PAGE_TITLE = "404 - Page Not Found";

const NotFoundPage = ({ inDashboard = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const headingRef = useRef(null);
  const isAuthenticated = Boolean(Cookies.get("authToken"));

  const homeTo = isAuthenticated ? "/dashboard" : "/login";
  const homeLabel = isAuthenticated ? "Back to Dashboard" : "Back to Login";

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    headingRef.current?.focus();

    return () => {
      document.title = previousTitle;
    };
  }, []);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(homeTo);
    }
  };

  const content = (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="bg-white/90 dark:bg-acs-dark-surface/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 dark:border-acs-dark-border p-fluid-4 max-w-[42rem] w-full"
    >
      <div className="flex flex-col items-center text-center gap-fluid-2 font-roboto">
        {!inDashboard && (
          <img
            src={ascendLogo}
            alt="Ascend Logo"
            className="w-[clamp(72px,10vw,110px)] mx-auto mb-fluid-3"
          />
        )}
        <p
          aria-hidden="true"
          className="text-[clamp(4.5rem,14vw,7rem)] font-bold leading-none tracking-tight text-acs-primary-navy dark:text-custom-bg-blue"
        >
          404
        </p>

        <div className="flex flex-col gap-fluid-2 max-w-md mx-auto w-full">
          <h1
            id="not-found-heading"
            ref={headingRef}
            tabIndex={-1}
            className="text-fluid-2xl font-bold text-acs-primary-navy dark:text-acs-dark-text text-center outline-none focus:outline-none"
          >
            Page Not Found
          </h1>
          <p className="text-fluid-base text-gray-600 dark:text-acs-dark-subtext text-left">
            Sorry, we couldn&apos;t find the page you&apos;re looking for. It may have been
            moved, deleted, or the URL may be incorrect.
          </p>
          <div className="text-left rounded-lg bg-gray-50/80 dark:bg-acs-dark-bg/60 px-fluid-2 py-fluid-1.5">
            <p className="text-fluid-xs text-gray-400 dark:text-acs-dark-subtext mb-fluid-0.5">
              Requested URL
            </p>
            <p className="text-fluid-sm text-gray-500 dark:text-acs-dark-subtext break-all font-mono">
              {location.pathname}
            </p>
          </div>
        </div>

        <div className="flex flex-col xs:flex-row items-center justify-center gap-fluid-2 w-full pt-fluid-1">
          <Button text={homeLabel} onClick={() => navigate(homeTo)} />
          <Button
            text="Go Back"
            variant="outline"
            onClick={handleGoBack}
            className="!w-auto min-w-[clamp(8rem,12vw,10rem)]"
          />
        </div>
      </div>
    </motion.div>
  );

  if (inDashboard) {
    return (
      <main
        className="flex flex-1 w-full h-full min-h-[70vh] items-center justify-center font-roboto px-fluid-4 bg-custom-bg-light-purple dark:bg-acs-dark-bg"
        aria-labelledby="not-found-heading"
      >
        {content}
      </main>
    );
  }

  return (
    <main
      className="min-h-screen w-full flex items-center justify-center bg-custom-bg-light-purple dark:bg-acs-dark-bg font-roboto px-fluid-4 transition-none"
      aria-labelledby="not-found-heading"
    >
      {content}
    </main>
  );
};

export default NotFoundPage;
